import { MonoTypeOperatorFunction, Observable, OperatorFunction, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, tap } from 'rxjs/operators';

export function bufferDebounceTime<T>(time = 0): OperatorFunction<T, T[]> {
  return (source: Observable<T>) => {
    let bufferedValues: T[] = [];

    return source.pipe(
      tap(value => bufferedValues.push(value)),
      debounceTime(time),
      map(() => bufferedValues),
      tap(() => bufferedValues = []),
    );
  };
}

export function distinctUntilArrayItemChanged<T>(itemComparator?: (a: T, b: T) => boolean): MonoTypeOperatorFunction<T[]> {
  itemComparator ??= (a, b) => a === b;
  return distinctUntilChanged((prevCollection: T[], currentCollection: T[]) => {
    if (prevCollection.length !== currentCollection.length) { return false; }
    return !currentCollection.some((item, i) => !itemComparator(prevCollection[i], item));
  });
}

export function distinctUntilArrayOrderChanged<T>(itemComparator?: (a: T, b: T) => boolean): MonoTypeOperatorFunction<T[]> {
  itemComparator ??= (a, b) => a === b;
  return distinctUntilChanged((prevCollection: T[], currentCollection: T[]) => {
    if (prevCollection.length !== currentCollection.length) { return false; }
    return currentCollection.every((item, i) => itemComparator(prevCollection[i], item));
  });
}

export function filterNil<T>(): OperatorFunction<T, NonNullable<T>> {
  return filter((value: T): value is NonNullable<T> => value !== null && value !== undefined);
}

export function distinctUntilPropertyChanged<T>(...keys: (keyof T)[]): MonoTypeOperatorFunction<T> {
  return distinctUntilChanged((a, b) => {
    if (a == b) { return true; }
    if ((!a && !!b) || (!!a && !b)) { return false; }
    const checkKeys = !keys?.length ? (Object.getOwnPropertyNames(a) as (keyof T)[]) : keys;
    return checkKeys.every(key => a?.[key] === b?.[key]);
  });
}

export function tapLog<T>(info: string): MonoTypeOperatorFunction<T> {
   
  return tap((x) => console.log(info, x));
}

export function tapIf<T>(predicate: (value: T, index: number) => boolean, tapAction: (value: T) => void, thisArg?: unknown): MonoTypeOperatorFunction<T> {
  return filter((value, index) => {
    if (predicate.call(thisArg, value, index)) { tapAction(value); }
    return true;
  });
}

export function rethrowError<T, E>(throwErr: E): MonoTypeOperatorFunction<T> {
  return catchError(() => throwError(() => throwErr));
}

